import { Injectable, inject } from '@angular/core';
import { AnalyticsService } from '../analytics/analytics.service';
import { Capacitor } from '@capacitor/core';
import { environment } from 'src/environments/environment';
import { EventName } from '../analytics/event-names';
import config from 'capacitor.config';
import { HttpService } from '../http/http.service';
import { BootupData } from '../../api/jit/schemas/bootup';
import { JitApi } from '../../api/jit/jit-api';
import { GameId } from 'src/app/model/game/game-id';

@Injectable({
  providedIn: 'root',
})
export class BootupService {
  private httpService = inject(HttpService);
  private analyticsService = inject(AnalyticsService);
  private bootupData: BootupData | undefined;

  public async initialise(): Promise<void> {
    this.bootupData = await JitApi.request(this.httpService, this.analyticsService).bootup();
    this.analyticsService.log(EventName.BootupRequestSuccess);
  }

  public updateRequired(): boolean {
    if (!this.bootupData) {
      return false;
    }

    if (Capacitor.isNativePlatform()) {
      if (Capacitor.getPlatform() === 'ios' && this.bootupData.minIOSVersion > environment.build) {
        return true;
      }
      if (
        Capacitor.getPlatform() === 'android' &&
        this.bootupData.minAndroidVersion > environment.build
      ) {
        return true;
      }
    } else if (this.bootupData.minWebVersion > environment.build) {
      location.reload();
    }
    return false;
  }

  public isReviewVersion(): boolean {
    if (!this.bootupData) {
      return false;
    }
    return this.bootupData.appleReviewVersion === config.plugins?.['CapacitorUpdater']?.['version'];
  }

  public allSupportedGames(): GameId[] {
    if (!this.bootupData) {
      return [];
    }
    const { featureFlags } = this.bootupData;

    const games = [
      [GameId.Bingo, featureFlags.enableBingo ?? false],
      [GameId.HiLo, featureFlags.enableHiLo ?? false],
      [GameId.VideoPoker, featureFlags.enableVideoPoker ?? false],
      [GameId.Marbles, featureFlags.enableMarbles ?? false],
      [GameId.Plinko, featureFlags.enablePlinko ?? false],
      [GameId.Slots, featureFlags.enableSlots ?? false],
      [GameId.SlotsBaseball, featureFlags.enableSlotsBaseball ?? false],
      [GameId.TwentyOne, featureFlags.enableTwentyOne ?? false],
      [GameId.Blackjack, featureFlags.enableBlackjack],
      [GameId.Blocks, featureFlags.enableBlocks],
      [GameId.Solitaire, featureFlags.enableSolitaire],
    ] satisfies [GameId, boolean][];

    return games.filter(([_, isEnabled]) => isEnabled).map(([gameId]) => gameId);
  }
}
